import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  Icon,
  Label,
  Message,
  Modal,
  Pagination,
  Segment,
  Table,
  Tab,
} from 'semantic-ui-react';
import { API, showError, showSuccess, timestamp2string } from '../../helpers';
import { ITEMS_PER_PAGE } from '../../constants';

const RecycleStation = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  
  // 已删除的渠道
  const [deletedChannels, setDeletedChannels] = useState([]);
  const [channelLoading, setChannelLoading] = useState(false);
  const [channelPage, setChannelPage] = useState(1);
  const [channelTotal, setChannelTotal] = useState(0);

  // 已删除的令牌
  const [deletedTokens, setDeletedTokens] = useState([]);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenPage, setTokenPage] = useState(1);
  const [tokenTotal, setTokenTotal] = useState(0);

  // 确认对话框
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmItem, setConfirmItem] = useState(null);
  const [confirmType, setConfirmType] = useState(''); // 'channel' or 'token'

  // 加载已删除的渠道
  const loadDeletedChannels = async (page = 1) => {
    setChannelLoading(true);
    try {
      // 使用专用的已删除渠道接口
      const res = await API.get(`/api/channel/deleted?p=${page - 1}`);
      if (res.data.success) {
        setDeletedChannels(res.data.data || []);
        setChannelTotal(res.data.total || 0);
      } else {
        showError(res.data.message);
      }
    } catch (err) {
      showError(err.message);
    }
    setChannelLoading(false);
  };

  // 加载已删除的令牌
  const loadDeletedTokens = async (page = 1) => {
    setTokenLoading(true);
    try {
      // 使用专用的已删除令牌接口
      const res = await API.get(`/api/token/deleted?p=${page - 1}`);
      if (res.data.success) {
        setDeletedTokens(res.data.data || []);
        setTokenTotal(res.data.total || 0);
      } else {
        showError(res.data.message);
      }
    } catch (err) {
      showError(err.message);
    }
    setTokenLoading(false);
  };

  useEffect(() => {
    loadDeletedChannels(1);
    loadDeletedTokens(1);
  }, []);

  // 恢复渠道
  const handleRestoreChannel = async (id) => {
    try {
      const res = await API.post(`/api/channel/restore/${id}`);
      if (res.data.success) {
        showSuccess(t('recycle.messages.channel_restored'));
        loadDeletedChannels(channelPage);
      } else {
        showError(res.data.message);
      }
    } catch (err) {
      showError(err.message);
    }
  };

  // 恢复令牌
  const handleRestoreToken = async (id) => {
    try {
      const res = await API.post(`/api/token/restore/${id}`);
      if (res.data.success) {
        showSuccess(t('recycle.messages.token_restored'));
        loadDeletedTokens(tokenPage);
      } else {
        showError(res.data.message);
      }
    } catch (err) {
      showError(err.message);
    }
  };

  // 彻底删除渠道
  const handlePermanentDeleteChannel = async (id) => {
    try {
      const res = await API.delete(`/api/channel/${id}`);
      if (res.data.success) {
        showSuccess(t('recycle.messages.channel_deleted'));
        loadDeletedChannels(channelPage);
      } else {
        showError(res.data.message);
      }
    } catch (err) {
      showError(err.message);
    }
  };

  // 彻底删除令牌
  const handlePermanentDeleteToken = async (id) => {
    try {
      const res = await API.delete(`/api/token/${id}`);
      if (res.data.success) {
        showSuccess(t('recycle.messages.token_deleted'));
        loadDeletedTokens(tokenPage);
      } else {
        showError(res.data.message);
      }
    } catch (err) {
      showError(err.message);
    }
  };

  // 显示确认对话框
  const showConfirm = (action, item, type) => {
    setConfirmAction(() => action);
    setConfirmItem(item);
    setConfirmType(type);
    setConfirmOpen(true);
  };

  // 确认操作
  const handleConfirm = async () => {
    setConfirmOpen(false);
    if (confirmAction) {
      await confirmAction(confirmItem.id);
    }
  };

  // 渠道表格
  const renderChannelsTable = () => {
    const totalPages = Math.ceil(channelTotal / ITEMS_PER_PAGE);

    return (
      <Table celled striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{t('recycle.col.id')}</Table.HeaderCell>
            <Table.HeaderCell>{t('recycle.col.name')}</Table.HeaderCell>
            <Table.HeaderCell>{t('recycle.col.type')}</Table.HeaderCell>
            <Table.HeaderCell>{t('recycle.col.models')}</Table.HeaderCell>
            <Table.HeaderCell>{t('recycle.col.deleted_at')}</Table.HeaderCell>
            <Table.HeaderCell>{t('recycle.col.actions')}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {deletedChannels.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan='6' textAlign='center'>
                {t('recycle.messages.no_deleted_channels')}
              </Table.Cell>
            </Table.Row>
          ) : (
            deletedChannels.map((channel) => (
              <Table.Row key={channel.id}>
                <Table.Cell>{channel.id}</Table.Cell>
                <Table.Cell>{channel.name}</Table.Cell>
                <Table.Cell>
                  <Label size='small'>{channel.type}</Label>
                </Table.Cell>
                <Table.Cell>
                  <code style={{ fontSize: '0.85em' }}>
                    {channel.models?.substring(0, 50)}
                    {channel.models?.length > 50 ? '...' : ''}
                  </code>
                </Table.Cell>
                <Table.Cell>{timestamp2string(channel.created_time)}</Table.Cell>
                <Table.Cell>
                  <Button
                    size='small'
                    positive
                    onClick={() => showConfirm(handleRestoreChannel, channel, 'channel')}
                  >
                    <Icon name='refresh' />
                    {t('recycle.buttons.restore')}
                  </Button>
                  <Button
                    size='small'
                    negative
                    onClick={() => showConfirm(handlePermanentDeleteChannel, channel, 'channel')}
                  >
                    <Icon name='trash' />
                    {t('recycle.buttons.permanent_delete')}
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))
          )}
        </Table.Body>
        {channelTotal > ITEMS_PER_PAGE && (
          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan='6'>
                <Pagination
                  activePage={channelPage}
                  totalPages={totalPages}
                  onPageChange={(e, { activePage }) => {
                    setChannelPage(activePage);
                    loadDeletedChannels(activePage);
                  }}
                  size='small'
                  floated='right'
                />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        )}
      </Table>
    );
  };

  // 令牌表格
  const renderTokensTable = () => {
    const totalPages = Math.ceil(tokenTotal / ITEMS_PER_PAGE);

    return (
      <Table celled striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{t('recycle.col.id')}</Table.HeaderCell>
            <Table.HeaderCell>{t('recycle.col.name')}</Table.HeaderCell>
            <Table.HeaderCell>{t('recycle.col.quota')}</Table.HeaderCell>
            <Table.HeaderCell>{t('recycle.col.used')}</Table.HeaderCell>
            <Table.HeaderCell>{t('recycle.col.deleted_at')}</Table.HeaderCell>
            <Table.HeaderCell>{t('recycle.col.actions')}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {deletedTokens.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan='6' textAlign='center'>
                {t('recycle.messages.no_deleted_tokens')}
              </Table.Cell>
            </Table.Row>
          ) : (
            deletedTokens.map((token) => (
              <Table.Row key={token.id}>
                <Table.Cell>{token.id}</Table.Cell>
                <Table.Cell>{token.name}</Table.Cell>
                <Table.Cell>
                  {token.unlimited_quota ? '无限' : token.remain_quota?.toFixed(2)}
                </Table.Cell>
                <Table.Cell>{token.used_quota?.toFixed(2)}</Table.Cell>
                <Table.Cell>{timestamp2string(token.created_time)}</Table.Cell>
                <Table.Cell>
                  <Button
                    size='small'
                    positive
                    onClick={() => showConfirm(handleRestoreToken, token, 'token')}
                  >
                    <Icon name='refresh' />
                    {t('recycle.buttons.restore')}
                  </Button>
                  <Button
                    size='small'
                    negative
                    onClick={() => showConfirm(handlePermanentDeleteToken, token, 'token')}
                  >
                    <Icon name='trash' />
                    {t('recycle.buttons.permanent_delete')}
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))
          )}
        </Table.Body>
        {tokenTotal > ITEMS_PER_PAGE && (
          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan='6'>
                <Pagination
                  activePage={tokenPage}
                  totalPages={totalPages}
                  onPageChange={(e, { activePage }) => {
                    setTokenPage(activePage);
                    loadDeletedTokens(activePage);
                  }}
                  size='small'
                  floated='right'
                />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        )}
      </Table>
    );
  };

  // Tab 配置
  const panes = [
    {
      menuItem: {
        key: 'channels',
        icon: 'server',
        content: t('recycle.tabs.deleted_channels'),
      },
      render: () => (
        <Tab.Pane loading={channelLoading}>
          {renderChannelsTable()}
        </Tab.Pane>
      ),
    },
    {
      menuItem: {
        key: 'tokens',
        icon: 'key',
        content: t('recycle.tabs.deleted_tokens'),
      },
      render: () => (
        <Tab.Pane loading={tokenLoading}>
          {renderTokensTable()}
        </Tab.Pane>
      ),
    },
  ];

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            <Icon name='trash outline' />
            {t('recycle.title')}
          </Card.Header>
          <Card.Description>
            {t('recycle.description')}
          </Card.Description>
        </Card.Content>
        <Card.Content>
          <Message info>
            <Message.Header>{t('recycle.messages.tip')}</Message.Header>
            <p>
              {t('recycle.messages.tip_desc1')}<br/>
              {t('recycle.messages.tip_desc2')}<br/>
              {t('recycle.messages.tip_desc3')}
            </p>
          </Message>

          <Tab
            panes={panes}
            activeIndex={activeTab}
            onTabChange={(e, { activeIndex }) => setActiveTab(activeIndex)}
          />
        </Card.Content>
      </Card>

      {/* 确认对话框 */}
      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        size='mini'
      >
        <Modal.Header>{t('recycle.confirm.title')}</Modal.Header>
        <Modal.Content>
          <p>
            {confirmType === 'channel' && confirmAction === handleRestoreChannel && (
              <>{t('recycle.confirm.restore_channel', { name: confirmItem?.name })}</>
            )}
            {confirmType === 'channel' && confirmAction === handlePermanentDeleteChannel && (
              <>{t('recycle.confirm.delete_channel', { name: confirmItem?.name })}</>
            )}
            {confirmType === 'token' && confirmAction === handleRestoreToken && (
              <>{t('recycle.confirm.restore_token', { name: confirmItem?.name })}</>
            )}
            {confirmType === 'token' && confirmAction === handlePermanentDeleteToken && (
              <>{t('recycle.confirm.delete_token', { name: confirmItem?.name })}</>
            )}
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setConfirmOpen(false)}>{t('recycle.buttons.cancel')}</Button>
          <Button
            color={confirmAction?.name?.includes('Permanent') ? 'red' : 'green'}
            onClick={handleConfirm}
          >
            <Icon name={confirmAction?.name?.includes('Permanent') ? 'trash' : 'check'} />
            {confirmAction?.name?.includes('Permanent') ? t('recycle.buttons.permanent_delete') : t('recycle.buttons.restore')}
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default RecycleStation;
